import {getFormattedEnvName, isIde} from "../../misc/envNameUtils.ts";
import {
    DEFAULT_MODEL,
    DEFAULT_CLAUDE_SONNET_REASONING_EFFORT,
    DEFAULT_THEME,
    DEFAULT_VOICE,
    DEFAULT_PRIMARY_LANGUAGE,
    DEFAULT_SECONDARY_LANGUAGE,
    DEFAULT_SEND_MESSAGE_PHRASE,
    DEFAULT_STOP_OUTPUT_PHRASE,
    DEFAULT_STOP_RECORDING_PHRASE,
    DEFAULT_CLAUDE_OPUS_REASONING_EFFORT
} from "../../../Globals.ts";
import {getUserInfo} from "./userInfo/getUserInfo.ts";
import {getSettings} from "./userSettings/getSettings.ts";
import {getUserStats} from "./userInfo/getUserStats.ts";
import {validateModelExists} from "../../misc/modelInsightUtils.ts";

export const setLoggedOutSettings = (setSettingsObj: any) => {
    setSettingsObj({
        model: DEFAULT_MODEL,
        voice: DEFAULT_VOICE,
        customInstructions: "",
        mode: "chat",
        claude_sonnet_reasoning_effort: DEFAULT_CLAUDE_SONNET_REASONING_EFFORT,
        claude_opus_reasoning_effort: DEFAULT_CLAUDE_OPUS_REASONING_EFFORT,
        apiKeyOnly: false
    });
}

export const setUnpaidSettings = (setSettingsObj: any) => {
    const unpaidSettingsObj: any = {
        model: DEFAULT_MODEL,
        mode: "chat",
        claude_sonnet_reasoning_effort: DEFAULT_CLAUDE_SONNET_REASONING_EFFORT,
        claude_opus_reasoning_effort: DEFAULT_CLAUDE_OPUS_REASONING_EFFORT,
        apiKeyOnly: false
    };

    setSettingsObj(unpaidSettingsObj);
}

const getDefaultForEnv = (environment: string, settingName: string) => {
    if (settingName === "contextMode") {
        if (environment === "Chrome") return "Image Only";
        else return null;
    } else if (settingName === "mode") {
        if (isIde(environment)) return "agent";
        else return "chat";
    }
};

export const setupUserState = async (user: any, setUserObj: any, setSettingsObj: any, setDynamicStateObj: any, environmentObjRef: any) => {
    const userInfo = await getUserInfo(user);
    getUserStats(user, setDynamicStateObj, environmentObjRef.current.environment);
    const userSettings = await getSettings(user, environmentObjRef.current.environment);
    let { subscriptionPlan, isBeta, remainingBalance, monthlyFreeMessages, previousPlans, hasUsageBasedEnabled } = userInfo;
    let formattedEnv = getFormattedEnvName(environmentObjRef.current.environment);

    const enableSearchInternet = userSettings.enableSearchInternet ?? false;
    const enableDeepResearch = userSettings.enableDeepResearch ?? false;
    const enableGenerateImages = userSettings.enableGenerateImages ?? false;
    const enableCreateDocument = userSettings.enableCreateDocument ?? false;
    const enableRunTerminalCommand = userSettings.enableRunTerminalCommand ?? false;
    const enableReviewChanges = userSettings.enableReviewChanges ?? false;
    const incognitoMode = userSettings.incognitoMode ?? false;
    const claude_sonnet_reasoning_effort = userSettings.claude_sonnet_reasoning_effort ?? DEFAULT_CLAUDE_SONNET_REASONING_EFFORT;
    const claude_opus_reasoning_effort = userSettings.claude_opus_reasoning_effort ?? DEFAULT_CLAUDE_OPUS_REASONING_EFFORT;
    const theme = userSettings.theme ?? DEFAULT_THEME;
    const autoTheme = userSettings.autoTheme ?? true;
    const model = validateModelExists(userSettings.defaultModel ?? DEFAULT_MODEL);
    const voice = userSettings.voice ?? DEFAULT_VOICE;
    const sendMessagePhrase = userSettings.sendMessagePhrase ?? DEFAULT_SEND_MESSAGE_PHRASE;
    const stopOutputPhrase = userSettings.stopOutputPhrase ?? DEFAULT_STOP_OUTPUT_PHRASE;
    const stopRecordingPhrase = userSettings.stopRecordingPhrase ?? DEFAULT_STOP_RECORDING_PHRASE;
    const primaryLanguage = userSettings.primaryLanguage ?? DEFAULT_PRIMARY_LANGUAGE;
    const secondaryLanguage = userSettings.secondaryLanguage ?? DEFAULT_SECONDARY_LANGUAGE;
    const customInstructions = userSettings.customInstructions ?? "";
    const mode = userSettings.mode ?? "agent";
    const customModes = userSettings.customModes ?? {};
    const scrapeErrors = userSettings.scrapeErrors ?? true;
    const scrapeWarnings = userSettings.scrapeWarnings ?? true;
    const projectCustomInstructions = userSettings.projectCustomInstructions ?? {};
    let contextMode;
    if (environmentObjRef.current.environment === "chrome") contextMode = userSettings.contextMode ?? getDefaultForEnv(formattedEnv, "contextMode");
    else contextMode = getDefaultForEnv(formattedEnv, "contextMode");

    const newSettingsObj: any = {
        autoTheme: autoTheme,
        customInstructions: customInstructions,
        enableSearchInternet: enableSearchInternet,
        enableDeepResearch: enableDeepResearch,
        enableGenerateImages: enableGenerateImages,
        enableCreateDocument: enableCreateDocument,
        enableRunTerminalCommand: enableRunTerminalCommand,
        enableReviewChanges: enableReviewChanges,
        isIncognito: incognitoMode,
        claude_sonnet_reasoning_effort: claude_sonnet_reasoning_effort,
        claude_opus_reasoning_effort: claude_opus_reasoning_effort,
        mode: mode,
        contextMode: contextMode,
        model: model,
        voice: voice,
        sendMessagePhrase: sendMessagePhrase,
        stopOutputPhrase: stopOutputPhrase,
        stopRecordingPhrase: stopRecordingPhrase,
        primaryLanguage: primaryLanguage,
        secondaryLanguage: secondaryLanguage,
        customModes: customModes,
        projectCustomInstructions: projectCustomInstructions,
        scrapeErrors: scrapeErrors,
        scrapeWarnings: scrapeWarnings
    }
    if (!autoTheme) {
        newSettingsObj['theme'] = theme
    }
    setSettingsObj(newSettingsObj);

    if (subscriptionPlan === null) {
        if (monthlyFreeMessages <= 0) {
            setUnpaidSettings(setSettingsObj);
        }

        setUserObj({remainingBalance, monthlyFreeMessages, subscriptionPlan: null, previousPlans, isBeta});
    } else {
        setUserObj({remainingBalance, monthlyFreeMessages: 0, subscriptionPlan, previousPlans, isBeta, hasUsageBasedEnabled});
    }

    return {subscriptionPlan, monthlyFreeMessages};
}
